if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.pendingScrollMediaId = null;
        this.pendingScrollToken = 0;
        this.pendingScrollTimeout = null;
        this.scrollSettleTimeout = null;
        this.scrollUpdateTimeout = null;
        if (!this.elements.viewer) return;

        this.elements.viewer.enableSliderLooping = true;
        if (this.elements.thumbnails) this.elements.thumbnails.enableSliderLooping = true;

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.addEventListener('click', this.onNavigationButtonClick.bind(this), true);
        this.elements.viewer.slider?.addEventListener('pointerdown', () => this.cancelPendingScroll(), {
          passive: true,
        });
        this.elements.viewer.slider?.addEventListener('scroll', this.onViewerScroll.bind(this), { passive: true });

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        this.updateGalleryState(this.getMediaItems().find((media) => media.classList.contains('is-active')));
      }

      disconnectedCallback() {
        this.clearPendingScroll();
        window.clearTimeout(this.scrollUpdateTimeout);
        this.scrollUpdateTimeout = null;
      }

      getMediaItems() {
        const mediaList = this.elements.viewer?.slider;
        if (!mediaList) return [];

        return Array.from(mediaList.children).filter((element) => element.matches('[data-media-id]'));
      }

      onSlideChanged(event) {
        const activeMedia = event.detail?.currentElement;
        if (!activeMedia?.dataset.mediaId) return;
        if (this.pendingScrollMediaId) return;

        if (!activeMedia.classList.contains('is-active')) {
          this.setActiveMedia(activeMedia.dataset.mediaId, false, { skipScroll: true });
          return;
        }

        this.updateGalleryState(activeMedia);
      }

      onViewerScroll() {
        if (!this.pendingScrollMediaId) return;

        const scrollToken = this.pendingScrollToken;
        window.clearTimeout(this.scrollSettleTimeout);
        this.scrollSettleTimeout = window.setTimeout(() => this.clearPendingScroll(scrollToken), 120);
      }

      onNavigationButtonClick(event) {
        const button = event.target.closest('[data-media-gallery-navigation]');
        if (!button || button.closest('media-gallery') !== this) return;

        event.preventDefault();
        event.stopPropagation();

        const mediaItems = this.getMediaItems();
        if (mediaItems.length <= 1) {
          this.updateGalleryState(mediaItems[0]);
          return;
        }

        const activeIndex = mediaItems.findIndex((media) => media.classList.contains('is-active'));
        const currentIndex = activeIndex >= 0 ? activeIndex : 0;
        const increment = button.dataset.mediaGalleryNavigation === 'next' ? 1 : -1;
        const targetIndex = (currentIndex + increment + mediaItems.length) % mediaItems.length;

        this.setActiveMedia(mediaItems[targetIndex].dataset.mediaId, false);
      }

      setActiveMedia(mediaId, prepend, { skipScroll = false, skipPageScroll = false } = {}) {
        const mediaItems = this.getMediaItems();
        const activeMedia =
          mediaItems.find((media) => media.dataset.mediaId === String(mediaId)) || mediaItems[0];
        if (!activeMedia) {
          return;
        }
        const resolvedMediaId = activeMedia.dataset.mediaId;
        mediaItems.forEach((element) => {
          element.classList.remove('is-active');
          element.removeAttribute('aria-current');
        });
        activeMedia.classList.add('is-active');
        activeMedia.setAttribute('aria-current', 'true');

        if (prepend) {
          activeMedia.parentElement.firstElementChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${resolvedMediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstElementChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          this.elements.viewer.resetPages?.();
          this.elements.thumbnails?.resetPages?.();
        }

        this.preventStickyHeader();
        if (!skipScroll) {
          window.clearTimeout(this.scrollUpdateTimeout);
          const scrollToken = this.startPendingScroll(resolvedMediaId);
          this.scrollUpdateTimeout = window.setTimeout(() => {
            this.scrollUpdateTimeout = null;
            if (scrollToken !== this.pendingScrollToken) return;

            if (!this.mql.matches || this.elements.thumbnails) {
              if (Math.abs(activeMedia.parentElement.scrollLeft - activeMedia.offsetLeft) > 1) {
                activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
              } else {
                this.clearPendingScroll(scrollToken);
              }
            } else {
              this.clearPendingScroll(scrollToken);
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (skipPageScroll || activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }
        this.playActiveMedia(activeMedia);
        this.updateGalleryState(activeMedia, true);
      }

      startPendingScroll(mediaId) {
        this.clearPendingScroll();
        this.pendingScrollToken += 1;
        this.pendingScrollMediaId = mediaId;
        const scrollToken = this.pendingScrollToken;
        this.pendingScrollTimeout = window.setTimeout(() => this.clearPendingScroll(scrollToken), 2500);
        return scrollToken;
      }

      cancelPendingScroll() {
        window.clearTimeout(this.scrollUpdateTimeout);
        this.scrollUpdateTimeout = null;
        this.pendingScrollToken += 1;
        this.clearPendingScroll();
      }

      clearPendingScroll(scrollToken) {
        if (scrollToken !== undefined && scrollToken !== this.pendingScrollToken) return;

        this.pendingScrollMediaId = null;
        window.clearTimeout(this.pendingScrollTimeout);
        this.pendingScrollTimeout = null;
        window.clearTimeout(this.scrollSettleTimeout);
        this.scrollSettleTimeout = null;
      }

      updateGalleryState(activeMedia, announce = false) {
        const mediaItems = this.getMediaItems();
        const resolvedActiveMedia = activeMedia || mediaItems.find((media) => media.classList.contains('is-active'));
        const activeIndex = resolvedActiveMedia ? mediaItems.indexOf(resolvedActiveMedia) : -1;
        const currentPosition = activeIndex >= 0 ? activeIndex + 1 : mediaItems.length > 0 ? 1 : 0;
        const activeThumbnail = resolvedActiveMedia?.dataset.mediaId
          ? this.elements.thumbnails?.querySelector(`[data-target="${resolvedActiveMedia.dataset.mediaId}"]`)
          : null;

        this.setActiveThumbnail(activeThumbnail);

        const currentCounter = this.elements.viewer.querySelector('[data-media-gallery-counter-current]');
        const totalCounter = this.elements.viewer.querySelector('[data-media-gallery-counter-total]');
        if (currentCounter) currentCounter.textContent = currentPosition;
        if (totalCounter) totalCounter.textContent = mediaItems.length;

        const hasSingleMedia = mediaItems.length <= 1;
        const navigationButtons = this.querySelectorAll('[data-media-gallery-navigation]');
        navigationButtons.forEach((button) => {
          button.toggleAttribute('disabled', hasSingleMedia);
          button.setAttribute('aria-disabled', hasSingleMedia ? 'true' : 'false');
        });

        new Set(Array.from(navigationButtons, (button) => button.closest('.slider-buttons'))).forEach((controls) => {
          if (controls) controls.hidden = hasSingleMedia;
        });

        if (announce && resolvedActiveMedia) {
          const imageItems = mediaItems.filter((media) => media.querySelector('.product__modal-opener--image'));
          const imagePosition = imageItems.indexOf(resolvedActiveMedia) + 1;
          this.announceLiveRegion(
            resolvedActiveMedia,
            activeThumbnail?.dataset.mediaPosition || imagePosition || currentPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((item) => {
          item.classList.remove('is-active');
          item.querySelector('button')?.removeAttribute('aria-current');
        });
        if (!thumbnail) return;

        thumbnail.classList.add('is-active');
        thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
